/**
 * The roles a person can have in the organization. Full explanation, and the
 * rules for changing any of this: docs/PERMISSIONS.md.
 *
 * Two working roles, on purpose — every role added is a column in every
 * permission decision from then on. `requested` is not a working role: it is
 * where someone who signed up without an invitation waits, with no access to
 * anything, until an admin chooses Admin or Staff for them.
 */
export const roles = ['admin', 'staff', 'requested'] as const
export type Role = (typeof roles)[number]

/** The roles an admin can give someone. Nobody is ever *made* "requested". */
export const assignableRoles = ['admin', 'staff'] as const satisfies readonly Role[]
export type AssignableRole = (typeof assignableRoles)[number]

export const roleLabels: Record<Role, string> = {
  admin: 'Admin',
  staff: 'Staff',
  requested: 'Requested',
}

export const roleDescriptions: Record<Role, string> = {
  admin: 'Everything, including Settings, Users and System.',
  staff: 'Everything except Settings and the pages under it.',
  requested: 'Signed up and waiting for an admin to let them in. Sees nothing yet.',
}

/**
 * Role names written before these two roles existed. They are still read, so
 * nobody's access changed on the day the roles were renamed and no stored row
 * had to be rewritten. docs/PERMISSIONS.md → "Old role names".
 */
export const legacyRoles: Record<string, Role> = {
  owner: 'admin',
  member: 'staff',
}

/**
 * Whatever is stored, as one of the roles. Anything unrecognised becomes
 * `requested` — no access at all — so a typo can never grant any.
 */
export function toRole(stored: string | null | undefined): Role {
  const value = stored ?? ''
  if ((roles as readonly string[]).includes(value)) return value as Role
  return legacyRoles[value] ?? 'requested'
}

export function isAssignable(role: Role): role is AssignableRole {
  return (assignableRoles as readonly Role[]).includes(role)
}
